/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  HostBinding,
  Input,
  Output,
  EventEmitter,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'umss-checkbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssCheckboxComponent),
      multi: true
    }
  ]
})
export class UmssCheckboxComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  private _generatedId = `umss-checkbox-${Math.random().toString(36).slice(2)}`;

  constructor() {}

  /** Content props */
  @Input() label: string = ''; // Checkbox label text
  @Input() id?: string; // Unique identifier for the checkbox
  @Input() name?: string; // Form name attribute
  @Input() value?: unknown; // Value for form submission

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular'; // Size variant

  /** State props */
  @Input() checked: boolean = false; // Checked state
  @Input() indeterminate: boolean = false; // Indeterminate state (for partial selections)
  @Input() disabled: boolean = false; // Disabled state
  @Input() required: boolean = false; // Required field indicator
  @Input() error: boolean = false; // Error state

  /** Event outputs */
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<Event>();

  /** ControlValueAccessor implementation */
  private onChange = (value: boolean) => {};
  private onTouched = () => {};

  writeValue(value: boolean): void {
    this.checked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newChecked = target.checked;
    this.indeterminate = false; // Clear indeterminate when user interacts

    // Update internal state for visual feedback
    this.checked = newChecked;

    // Emit events
    this.checkedChange.emit(newChecked);
    this.change.emit(event);

    // ControlValueAccessor
    this.onChange(newChecked);
    this.onTouched();
  }

  /** Computed properties for accessibility */
  get computedId(): string {
    return this.id || this._generatedId;
  }

  get ariaDescribedBy(): string | undefined {
    if (this.error) {
      return `${this.computedId}-error`;
    }
    return undefined;
  }
}
